/**
 * Element rendered at `/:tenantSlug/:subtreeSlug/reset-password` and
 * `/:tenantSlug/:subtreeSlug/login` — the app-space shape an emailed link to a
 * SUB-organization's page takes on a vanity host (item 96, 2026-11-04).
 *
 * jewishpro.ai is the vanity host of m2-team: the slug-hiding history reads
 * every browser path as m2-team's, so an invitation for a user of Orange County
 * (`jewishpro.ai/orange-county/reset-password?token=…`) arrived here as
 * `/m2-team/orange-county/reset-password`, matched nothing and fell to the
 * login page — the token never checked, the person never able to set a
 * password. Every email already sent has that shape, so it is served here.
 *
 * On a vanity host, for the host's own slug: ask the SERVER whether
 * `subtreeSlug` is the host's tenant or lies under it (`isServedOnHost`); if it
 * is, serve that organization's pages from now on in this tab
 * (`serveSubtreeSlug`) and re-enter the same page under its own code, keeping
 * the query string (the token) — PublicTenantResetPassword / PublicTenantLogin
 * then take it as on any host. Not in the subtree, unknown, or another host:
 * where it always went, the catch-all's `/`. The server unreachable: ask again
 * (1, 2, 4, 8 s …) — the page waits rather than drop the emailed token.
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { isServedOnHost } from './tenantResolver';
import { serveSubtreeSlug, vanityHostSlug } from './vanityHistory';

export type SubtreeEntry = 'reset-password' | 'login';

export function VanitySubtreeEntry({ entry }: { entry: SubtreeEntry }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug = '', subtreeSlug = '' } = useParams<{ tenantSlug?: string; subtreeSlug?: string }>();
  const code = subtreeSlug.toLowerCase();
  const hostSlug = vanityHostSlug();
  const onHost = hostSlug !== null && tenantSlug.toLowerCase() === hostSlug && code !== '';
  const [served, setServed] = useState<boolean | null>(onHost ? null : false);

  useEffect(() => {
    if (!onHost) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setServed(null);
    const ask = (attempt: number) => {
      isServedOnHost(code, window.location.host).then((answer) => {
        if (cancelled) return;
        if (answer === null) timer = setTimeout(() => ask(attempt + 1), Math.min(8000, 1000 * 2 ** attempt));
        else setServed(answer);
      });
    };
    ask(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [onHost, code]);

  useEffect(() => {
    if (!served) return;
    serveSubtreeSlug(code);
    navigate({ pathname: `/${code}/${entry}`, search: location.search, hash: location.hash }, { replace: true });
  }, [served, code, entry, location.search, location.hash, navigate]);

  if (served === false) return <Navigate to="/" replace />;
  return <LoadingScreen />;
}
